import React, { useCallback, useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Grid } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import ConsumerCheckoutLoginModalContent from './ConsumerCheckoutLoginModalContent'
import ConsumerCheckoutCreateAccountModalContent from './ConsumerCheckoutCreateAccountModalContent'
import ReCAPTCHA from 'react-google-recaptcha'
import isEmpty from 'lodash/isEmpty'
import { useMutation, useQueryClient } from 'react-query'
import { sendCartLoginSignupStatusToDataLayer } from '../../../utils/dataLayer'
import { signinSchema, signupSchema } from '../../formValidationSchemas'
import axios from 'axios'
import {
    collectFormErrors,
    removeErrorFromPath,
} from '../../../utils/formErrorUtils'
import { RECAPTCHA_RESUBMIT_FUNCTION } from '../ConsumerCheckoutConstants'

const useStyles = makeStyles((theme) => ({
    container: {
        maxWidth: 'none',
        margin: '0 auto',
        [theme.breakpoints.up('sm')]: {
            maxWidth: '90%',
        },
    },
    tab: {
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: '#f4f4f4',
        color: '#8a8a8a',
    },
    tabSelected: {
        backgroundColor: '#661b6b',
        fontSize: '1.5rem',
        color: 'white',
    },
    tabButton: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: 'inherit',
        textTransform: 'none',
    },
    recaptcha: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    },
}))

const SIGNUP_DETAILS_EMPTY = {
    name: '',
    email: '',
    password: '',
    newsletterSignup: false,
}

const LOGIN_DETAILS_EMPTY = {
    email: '',
    password: '',
}

const axiosInstance = axios.create({
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
})

const ConsumerCheckoutLoginCreateAccountModalContent = (props) => {
    const {
        modalIsOpen,
        baseUrl,
        facebookLoginUrl,
        googleLoginUrl,
        loginFormErrors,
        signupFormErrors,
        processing,
        errors,
        setErrors,
        handleShowGuestCheckout,
        guestEmail,
        setGuestEmail,
        cartHasVirtualVisaWithTotalBalanceMoreThanThreshold,
        recaptchaEnabled,
        recaptchaSiteKey,
        setProcessing,
        setShowCardError,
        setSignupFormErrors,
        setLoginFormErrors,
        clearErrors,
    } = props

    const classes = useStyles()
    const queryClient = useQueryClient()

    const [showLogin, setShowLogin] = useState(true)
    const [showCreateAccount, setShowCreateAccount] = useState(false)
    const [recaptchaResubmitFunction, setRecaptchaResubmitFunction] =
        useState('')
    const [signupDetails, setSignupDetails] = useState(SIGNUP_DETAILS_EMPTY)
    const [loginDetails, setLoginDetails] = useState(LOGIN_DETAILS_EMPTY)

    const recaptchaRef = useRef()

    const { mutate: submitLogin } = useMutation(
        (payload) => axiosInstance.post('/ajax/user-login/', payload),
        {
            onSuccess: () => {
                sendCartLoginSignupStatusToDataLayer('cartLogin', 'success')
                setProcessing(false)
                queryClient
                    .invalidateQueries('userAndPaymentMethods')
                    .catch(/* TODO: Add console.error() */)
            },
            onError: () => {
                sendCartLoginSignupStatusToDataLayer('cartLogin', 'failed')
                setProcessing(false)
                setShowCardError(true) // True enables the potential display after user has manually closed the alert previously
                const errorMessage = 'The username or password you entered was incorrect. Please try again or click "Forgot password?"'
                setErrors([errorMessage])
            },
        }
    )

    const { mutate: createAccount } = useMutation(
        (payload) => axiosInstance.post('/ajax/signup/', payload),
        {
            onSuccess: () => {
                sendCartLoginSignupStatusToDataLayer('cartSignup', 'success')
                setProcessing(false)
                queryClient
                    .invalidateQueries('userAndPaymentMethods')
                    .catch(/* TODO: Add console.error() */)
            },
            onError: () => {
                sendCartLoginSignupStatusToDataLayer('cartSignup', 'failed')
                setProcessing(false)
                setShowCardError(true) // True enables the potential display after user has manually closed the alert previously
                const errorMessage =
                    'This email address is reserved by a registered account.'
                setErrors([errorMessage])
            },
        }
    )

    async function validateLoginFormField(path, data) {
        return await signinSchema.validateAt(path, data, {
            abortEarly: false,
        })
    }

    async function validateSignupFormField(path, data) {
        return await signupSchema.validateAt(path, data, {
            abortEarly: false,
        })
    }

    const validateLoginField = (path, data) => {
        validateLoginFormField(path, { [path]: data })
            .then(function () {
                setLoginFormErrors(removeErrorFromPath(path, loginFormErrors))
            })
            .catch(function (e) {
                collectFormErrors(e, loginFormErrors, setLoginFormErrors)
            })
    }

    const validateSignupField = (path, data) => {
        validateSignupFormField(path, { [path]: data })
            .then(function () {
                setSignupFormErrors(removeErrorFromPath(path, signupFormErrors))
            })
            .catch(function (e) {
                collectFormErrors(e, signupFormErrors, setSignupFormErrors)
            })
    }

    const validateSignupForm = useCallback(async (signupDetails) => {
        return await signupSchema.validate(
            {
                email: signupDetails.email,
                password: signupDetails.password,
                name: signupDetails.name,
            },
            { abortEarly: false }
        )
    }, [])

    const handleShowLogin = useCallback(() => {
        setErrors([])
        setShowCreateAccount(false)
        setShowLogin(true)
    }, [setErrors])

    const handleShowCreateAccount = useCallback(() => {
        setErrors([])
        setShowLogin(false)
        setShowCreateAccount(true)
    }, [setErrors])

    const handleUserSignup = useCallback(
        async (recaptchaToken) => {
            let currentErrors = {}
            try {
                await validateSignupForm(signupDetails)
            } catch (e) {
                e.inner.forEach(function (item) {
                    currentErrors[item.path] = item.errors
                })
            }

            if (!isEmpty(currentErrors)) {
                setProcessing(false)
                setSignupFormErrors(currentErrors)
                return
            }

            if (recaptchaEnabled === '1' && !recaptchaToken) {
                setRecaptchaResubmitFunction(RECAPTCHA_RESUBMIT_FUNCTION.SIGNUP)
                recaptchaRef?.current?.execute()
            } else {
                setProcessing(true)
                clearErrors()
                createAccount({
                    email: signupDetails.email,
                    password: signupDetails.password,
                    name: signupDetails.name,
                    newsletterSignup: signupDetails.newsletterSignup,
                    recaptchaToken: recaptchaToken,
                })
            }
        },
        [
            validateSignupForm,
            createAccount,
            signupDetails,
            clearErrors,
            recaptchaEnabled,
            setProcessing,
            setSignupFormErrors,
        ]
    )

    const validateLoginForm = useCallback(async (loginDetails) => {
        return await signinSchema.validate(
            {
                email: loginDetails.email,
                password: loginDetails.password,
            },
            { abortEarly: false }
        )
    }, [])

    const handleUserLogin = useCallback(
        async (recaptchaToken) => {
            let currentErrors = {}
            try {
                await validateLoginForm(loginDetails)
            } catch (e) {
                e.inner.forEach(function (item) {
                    currentErrors[item.path] = item.errors
                })
            }

            if (!isEmpty(currentErrors)) {
                setProcessing(false)
                setLoginFormErrors(currentErrors)
                return
            }

            if (recaptchaEnabled === '1' && !recaptchaToken) {
                setRecaptchaResubmitFunction(RECAPTCHA_RESUBMIT_FUNCTION.LOGIN)
                recaptchaRef?.current?.execute()
            } else {
                setProcessing(true)
                clearErrors()
                submitLogin({
                    email: loginDetails.email,
                    password: loginDetails.password,
                    recaptchaToken: recaptchaToken,
                })
            }
        },
        [
            validateLoginForm,
            recaptchaEnabled,
            recaptchaRef,
            submitLogin,
            loginDetails,
            clearErrors,
            setLoginFormErrors,
            setProcessing
        ]
    )

    useEffect(() => {
        if (errors.length > 0 && recaptchaRef?.current) {
            // Reset the recaptcha token on failed api requests

            setTimeout(() => {
                try {
                    recaptchaRef?.current?.reset()
                } catch (error) {
                    console.error(
                        'Error resetting reCAPTCHA on checkout page:',
                        error
                    )
                }
            }, 1000)
        }
    }, [errors])

    return (
        <Grid item xs={12}>
            <Grid container justify="space-between">
                <Grid
                    item
                    xs={6}
                    className={`${classes.tab} ${
                        showLogin ? classes.tabSelected : ''
                    }`}
                    style={{ borderRadius: '1.5rem 0 0 1.5rem' }}
                >
                    <Button
                        className={classes.tabButton}
                        fullWidth
                        size="large"
                        onClick={handleShowLogin}
                        type={'button'}
                    >
                        Log In
                    </Button>
                </Grid>
                <Grid
                    item
                    xs={6}
                    className={`${classes.tab} ${
                        showCreateAccount ? classes.tabSelected : ''
                    }`}
                    style={{ borderRadius: '0 1.5rem 1.5rem 0' }}
                >
                    <Button
                        className={classes.tabButton}
                        fullWidth
                        size="large"
                        onClick={handleShowCreateAccount}
                        type={'button'}
                    >
                        Register
                    </Button>
                </Grid>
            </Grid>
            <div className={classes.container}>
                {showLogin && (
                    <ConsumerCheckoutLoginModalContent
                        baseUrl={baseUrl}
                        facebookLoginUrl={facebookLoginUrl}
                        googleLoginUrl={googleLoginUrl}
                        loginDetails={loginDetails}
                        setLoginDetails={setLoginDetails}
                        loginFormErrors={loginFormErrors}
                        processing={processing}
                        errors={errors}
                        handleShowGuestCheckout={handleShowGuestCheckout}
                        guestEmail={guestEmail}
                        setGuestEmail={setGuestEmail}
                        handleShowCreateAccount={handleShowCreateAccount}
                        validateLoginField={validateLoginField}
                        handleUserLogin={handleUserLogin}
                        cartHasVirtualVisaWithTotalBalanceMoreThanThreshold={
                            cartHasVirtualVisaWithTotalBalanceMoreThanThreshold
                        }
                    />
                )}
                {showCreateAccount && (
                    <ConsumerCheckoutCreateAccountModalContent
                        facebookLoginUrl={facebookLoginUrl}
                        googleLoginUrl={googleLoginUrl}
                        signupDetails={signupDetails}
                        setSignupDetails={setSignupDetails}
                        signupFormErrors={signupFormErrors}
                        processing={processing}
                        errors={errors}
                        handleShowLogin={handleShowLogin}
                        validateSignupField={validateSignupField}
                        handleUserSignup={handleUserSignup}
                        baseUrl={baseUrl}
                    />
                )}
            </div>
            {modalIsOpen && recaptchaEnabled === '1' && (
                <div className={classes.recaptcha}>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={recaptchaSiteKey}
                        size={'invisible'}
                        badge={'bottomright'}
                        onChange={async (recaptchaToken) => {
                            console.log('recaptcha onChange', {recaptchaResubmitFunction})
                            switch (recaptchaResubmitFunction) {
                                case RECAPTCHA_RESUBMIT_FUNCTION.LOGIN:
                                    console.log('recaptcha onChange', {case: RECAPTCHA_RESUBMIT_FUNCTION.LOGIN})
                                    try {
                                        await handleUserLogin(recaptchaToken)
                                    } catch (error) {
                                        console.error(
                                            `recaptcha resubmit handler error ${RECAPTCHA_RESUBMIT_FUNCTION.LOGIN}`,
                                            error
                                        )
                                    }
                                    return
                                case RECAPTCHA_RESUBMIT_FUNCTION.SIGNUP:
                                    console.log('recaptcha onChange', {case: RECAPTCHA_RESUBMIT_FUNCTION.SIGNUP})
                                    try {
                                        await handleUserSignup(recaptchaToken)
                                    } catch (error) {
                                        console.error(
                                            `recaptcha resubmit handler error ${RECAPTCHA_RESUBMIT_FUNCTION.SIGNUP}`,
                                            error
                                        )
                                    }
                                    return
                                default:
                                    console.log('recaptcha onChange', {case: 'default'})
                                    return
                            }
                        }}
                        onExpired={() => {
                            console.log('recaptcha onExpired', recaptchaRef?.current)
                            recaptchaRef?.current?.reset()
                        }}
                        onErrored={() => {
                            console.log('recaptcha onErrored', recaptchaRef?.current)
                            Sentry.captureMessage('ReCAPTCHA Errored', {
                                user: {
                                    id: JSON.stringify(
                                        Cookies.get('GCG_CLIENT_USER_ID')
                                    ),
                                },
                            })
                            recaptchaRef?.current?.reset()
                        }}
                    />
                </div>
            )}
        </Grid>
    )
}

export default ConsumerCheckoutLoginCreateAccountModalContent
